import type { AuthState } from "@/data/types/auth";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { authService } from "@/services/auth/auth-service/auth-service";
import { useState, useRef, type ReactNode } from "react";

import { AuthContext } from "./AuthContext.shared";
import { AUTH_INIT_TIMEOUT_MS, INITIAL_STATE } from "./constants";
import { useAuthActions } from "./hooks/use-auth-actions";
import { resolveAuthState } from "./utils/resolve-auth-state";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(INITIAL_STATE);
  const hasInitialized = useRef(false);
  const actions = useAuthActions(setAuthState);

  useMountEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const init = async () => {
      try {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Auth initialization timeout")),
            AUTH_INIT_TIMEOUT_MS
          )
        );

        const result = await Promise.race([resolveAuthState(), timeoutPromise]);
        setAuthState(result);

        if (result.isAuthenticated) {
          authService.startProactiveRenewal();
        }
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tokens: null,
          error:
            error instanceof Error
              ? error.message
              : "Authentication initialization failed",
        });
      }
    };

    init();
  });

  return (
    <AuthContext.Provider value={{ ...authState, ...actions }}>
      {children}
    </AuthContext.Provider>
  );
};
